import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Editor from '@monaco-editor/react';
import { Upload, Edit3, Code, X } from 'lucide-react';
import UtmGeometryPreview from './UtmGeometryPreview';
import UtmDrawingModal from './UtmDrawingModal';
import {
  processGeometryFile,
  parseGeometryFromEditor,
  formatGeometryForEditor,
} from '@/utils/utm-geometry';

interface UtmGeometryUploadProps {
  geometry: any;
  onChange: (geometry: any) => void;
  error?: string;
}

const UtmGeometryUpload = ({ geometry, onChange, error }: UtmGeometryUploadProps) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'draw' | 'json'>('upload');
  const [jsonText, setJsonText] = useState(() => formatGeometryForEditor(geometry));
  const [jsonError, setJsonError] = useState<string | undefined>();
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [drawingModalOpen, setDrawingModalOpen] = useState(false);

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploadError(undefined);

    const result = await processGeometryFile(file);

    if (result.error) {
      setUploadError(result.error);
      return;
    }

    if (result.geometry) {
      onChange(result.geometry);
      setJsonText(formatGeometryForEditor(result.geometry));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json', '.geojson', '.topojson'],
      'application/vnd.google-earth.kmz': ['.kmz'],
      'application/vnd.google-earth.kml+xml': ['.kml'],
    },
    multiple: false,
  });

  const handleJsonChange = (value: string | undefined) => {
    if (!value) return;

    setJsonText(value);
    setJsonError(undefined);

    const result = parseGeometryFromEditor(value);

    if (result.error) {
      setJsonError(result.error);
      return;
    }

    if (result.geometry) {
      onChange(result.geometry);
    }
  };

  const clearGeometry = () => {
    onChange(null);
    setJsonText('');
    setJsonError(undefined);
    setUploadError(undefined);
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        <button
          type="button"
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'upload'
              ? 'text-foreground border-b-2 border-primary'
              : 'text-muted-foreground hover:text-accent-foreground'
          }`}
        >
          <Upload className="inline mr-2" size={16} />
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('draw')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'draw'
              ? 'text-foreground border-b-2 border-primary'
              : 'text-muted-foreground hover:text-accent-foreground'
          }`}
        >
          <Edit3 className="inline mr-2" size={16} />
          Draw on Map
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('json')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'json'
              ? 'text-foreground border-b-2 border-primary'
              : 'text-muted-foreground hover:text-accent-foreground'
          }`}
        >
          <Code className="inline mr-2" size={16} />
          JSON Editor
        </button>
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-border bg-muted/50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto text-muted-foreground mb-3" size={40} />
              <p className="text-sm text-foreground mb-1">
                {isDragActive ? 'Drop the file here' : 'Drag & drop a geometry file'}
              </p>
              <p className="text-xs text-muted-foreground">Supports GeoJSON, TopoJSON, KML, KMZ</p>
            </div>
            {uploadError && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/50 rounded text-sm text-destructive">
                {uploadError}
              </div>
            )}
          </div>
        )}

        {/* Draw Tab */}
        {activeTab === 'draw' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Use the drawing modal to create complex geometries with multiple features.
            </p>
            <button
              type="button"
              onClick={() => setDrawingModalOpen(true)}
              className="w-full px-4 py-3 bg-primary hover:bg-primary text-primary-foreground rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Edit3 size={20} />
              <span>Open Drawing Modal</span>
            </button>
            {geometry && (
              <div className="text-sm text-foreground bg-muted p-3 rounded">
                <strong>Current geometry:</strong> {geometry.type}
              </div>
            )}
          </div>
        )}

        {/* JSON Tab */}
        {activeTab === 'json' && (
          <div className="space-y-2">
            <div className="h-72 rounded-lg overflow-hidden border border-border sm:h-96">
              <Editor
                height="100%"
                defaultLanguage="json"
                value={jsonText}
                onChange={handleJsonChange}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  formatOnPaste: true,
                  formatOnType: true,
                }}
              />
            </div>
            {jsonError && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 rounded text-sm text-destructive">
                {jsonError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/50 rounded text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Preview */}
      {geometry && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Geometry Preview</label>
            <button
              type="button"
              onClick={clearGeometry}
              className="flex items-center space-x-1 px-3 py-1 text-xs bg-red-500/10 text-destructive border border-red-500/50 rounded hover:bg-red-500/20 transition-colors"
            >
              <X size={14} />
              <span>Clear</span>
            </button>
          </div>
          <UtmGeometryPreview geometry={geometry} />
        </div>
      )}

      {/* Drawing Modal */}
      <UtmDrawingModal
        isOpen={drawingModalOpen}
        initialGeometry={geometry}
        onClose={() => setDrawingModalOpen(false)}
        onSave={(newGeometry) => {
          onChange(newGeometry);
          setJsonText(formatGeometryForEditor(newGeometry));
          setDrawingModalOpen(false);
        }}
      />
    </div>
  );
};

export default UtmGeometryUpload;
